import type { Pergunta, RespostaIrp } from "./irproposta";

// A calculadora de honorários do IRPF — a REGRA, sem tela e sem rede.
//
// Puro de propósito, como `extenso.ts`, `mascaras.ts` e `preencherContrato.ts`:
// tudo aqui é função de entrada para saída, e `app/tests/calculadora.mjs` prova
// os números fora do navegador. Um preço que vai para dentro de um contrato não
// pode depender de alguém abrir a tela para descobrir se está certo.
//
// ---------------------------------------------------------------------------
// De onde a regra vem
// ---------------------------------------------------------------------------
// Do repositório `calculadora-irpf` (feito no Lovable), arquivo
// `src/lib/calculadora.ts`. As cinco decisões dele estão preservadas inteiras, e
// o cabeçalho da migration 000071 as lista uma por uma. Duas merecem repetição
// aqui, porque quem lê este arquivo vai querer "consertá-las":
//
//   1. Situação TÉCNICA soma duas vezes — ela empurra a base para Complexa E
//      cobra o adicional dela. É o desenho de lá. Não é bug.
//   2. Os valores NÃO têm padrão. O commit a5279c8 daquele repositório zerou os
//      treze números sugeridos da primeira versão, de propósito: "a ferramenta
//      serve como referência estratégica de precificação, não como tabela fixa
//      nacional". Semear aqui o que foi apagado lá desfaz a decisão.
//
// ---------------------------------------------------------------------------
// O que este arquivo NÃO faz
// ---------------------------------------------------------------------------
//   * não lê nem escreve nada — quem grava é `irp_salvar_calculo`, que confere a
//     aritmética do que recebe (ver o comentário da 000071 sobre por que a conta
//     mora aqui e a conferência mora lá);
//   * não decide qual pergunta alimenta qual campo. Esse mapa é DADO
//     (`irp_mapa_calculo()`), e chega como argumento.

// ---------------------------------------------------------------------------
// Os treze valores da região
// ---------------------------------------------------------------------------
export type Parametros = {
  base_simples: number;
  base_intermediaria: number;
  base_complexa: number;
  ad_dependente: number;
  ad_fonte_renda: number;
  ad_aluguel: number;
  ad_ganho_capital: number;
  ad_bolsa: number;
  ad_cripto: number;
  ad_carne_leao: number;
  ad_atividade_rural: number;
  ad_exterior: number;
  ad_retificacao: number;
};

/** Tudo zero — o estado de quem nunca preencheu, e o único padrão que existe. */
export const PARAMETROS_VAZIOS: Parametros = {
  base_simples: 0,
  base_intermediaria: 0,
  base_complexa: 0,
  ad_dependente: 0,
  ad_fonte_renda: 0,
  ad_aluguel: 0,
  ad_ganho_capital: 0,
  ad_bolsa: 0,
  ad_cripto: 0,
  ad_carne_leao: 0,
  ad_atividade_rural: 0,
  ad_exterior: 0,
  ad_retificacao: 0,
};

/** As três bases preenchidas. Adicional zerado é escolha; base zerada é branco. */
export function parametrosPreenchidos(p: Parametros): boolean {
  return p.base_simples > 0 && p.base_intermediaria > 0 && p.base_complexa > 0;
}

// ---------------------------------------------------------------------------
// A situação do cliente
// ---------------------------------------------------------------------------
export type Posicionamento = "abaixo" | "media" | "acima";

export const POSICIONAMENTOS = [
  { chave: "abaixo", rotulo: "Abaixo da média", pct: -0.1 },
  { chave: "media", rotulo: "Na média", pct: 0 },
  { chave: "acima", rotulo: "Acima da média", pct: 0.15 },
] as const;

export type CampoSituacao =
  | "aluguel"
  | "ganho_capital"
  | "bolsa"
  | "cripto"
  | "carne_leao"
  | "atividade_rural"
  | "exterior"
  | "retificacao";

/** Todo campo que o mapa do banco pode apontar. */
export type CampoSimulacao = CampoSituacao | "dependentes";

export type Simulacao = {
  dependentes: number;
  fontes_renda: number;
  posicionamento: Posicionamento;
} & Record<CampoSituacao, boolean>;

/**
 * As oito situações especiais, em UMA lista.
 *
 * No Lovable a mesma lista aparece três vezes — uma para classificar, uma para
 * somar, uma para desenhar —, e a ordem entre elas já divergia: a frase
 * "Situações técnicas: …" saía em ordem diferente da tabela de adicionais. Aqui
 * é uma lista só, e a ordem dela vale para os três usos. O que muda em relação a
 * lá é a ordem das palavras numa frase; o que se ganha é não ter três listas
 * para manter iguais.
 *
 * `tecnica` é o que empurra a classificação para Complexa.
 */
export const SITUACOES: ReadonlyArray<{
  campo: CampoSituacao;
  rotulo: string;
  parametro: keyof Parametros;
  tecnica: boolean;
}> = [
  { campo: "aluguel", rotulo: "Rendimentos de aluguel", parametro: "ad_aluguel", tecnica: false },
  { campo: "ganho_capital", rotulo: "Ganho de capital", parametro: "ad_ganho_capital", tecnica: true },
  { campo: "bolsa", rotulo: "Operações em bolsa", parametro: "ad_bolsa", tecnica: true },
  { campo: "cripto", rotulo: "Criptoativos", parametro: "ad_cripto", tecnica: true },
  { campo: "carne_leao", rotulo: "Carnê-leão", parametro: "ad_carne_leao", tecnica: false },
  { campo: "atividade_rural", rotulo: "Atividade rural", parametro: "ad_atividade_rural", tecnica: true },
  { campo: "exterior", rotulo: "Rendimentos no exterior", parametro: "ad_exterior", tecnica: true },
  { campo: "retificacao", rotulo: "Retificação de declaração", parametro: "ad_retificacao", tecnica: false },
];

/**
 * O ponto de partida.
 *
 * `fontes_renda: 1`, e não 0 como no Lovable — lá o estado inicial dizia "0
 * fontes de renda" num campo cujo próprio `min` era 1. Quem declara IR tem ao
 * menos uma fonte; zero é um número que a tela mostrava e a regra nunca usava.
 */
export const SIMULACAO_INICIAL: Simulacao = {
  dependentes: 0,
  fontes_renda: 1,
  posicionamento: "media",
  aluguel: false,
  ganho_capital: false,
  bolsa: false,
  cripto: false,
  carne_leao: false,
  atividade_rural: false,
  exterior: false,
  retificacao: false,
};

// ---------------------------------------------------------------------------
// A classificação
// ---------------------------------------------------------------------------
export type Classificacao = "Simples" | "Intermediária" | "Complexa";

export type Item = { descricao: string; valor: number };

export type Resultado = {
  classificacao: Classificacao;
  motivo: string;
  base: number;
  adicionais: Item[];
  adicionais_total: number;
  posicionamento: Posicionamento;
  posicionamento_rotulo: string;
  posicionamento_pct: number;
  ajuste: number;
  total: number;
  faixa_min: number;
  faixa_max: number;
  observacao: string;
};

const OBSERVACOES: Record<Classificacao, string> = {
  Simples:
    "Declaração de baixa complexidade. Ideal para clientes com rendimentos CLT únicos, sem situações especiais.",
  Intermediária:
    "Declaração com complexidade moderada. Envolve fatores adicionais que demandam mais atenção na análise.",
  Complexa:
    "Declaração de alta complexidade técnica. Requer conhecimento especializado e maior tempo de elaboração.",
};

export function classificar(s: Simulacao): { classificacao: Classificacao; motivo: string } {
  const tecnicas = SITUACOES.filter((x) => x.tecnica && s[x.campo]);
  if (tecnicas.length > 0) {
    return {
      classificacao: "Complexa",
      motivo: `Situações técnicas: ${tecnicas.map((x) => x.rotulo).join(", ")}`,
    };
  }

  const fatores: string[] = [];
  if (s.dependentes > 0) {
    fatores.push(`${s.dependentes} ${s.dependentes === 1 ? "dependente" : "dependentes"}`);
  }
  if (s.fontes_renda > 1) fatores.push(`${s.fontes_renda} fontes de renda`);
  for (const x of SITUACOES) {
    if (!x.tecnica && s[x.campo]) fatores.push(x.rotulo);
  }

  if (fatores.length > 0) {
    return { classificacao: "Intermediária", motivo: `Fatores: ${fatores.join(", ")}` };
  }
  return { classificacao: "Simples", motivo: "Declaração sem complexidades adicionais" };
}

// ---------------------------------------------------------------------------
// A conta
// ---------------------------------------------------------------------------
/**
 * Arredondamento para centavos, em UM lugar.
 *
 * O Lovable não arredonda: `(base + adicionais) * 0.15` produz fração de
 * centavo, a tela esconde isso no `toLocaleString` e o número real segue com
 * casas invisíveis. Aqui o valor é gravado numa coluna `numeric(10,2)` e
 * conferido pela `irp_salvar_calculo` — se a tela mostrasse 408,25 e o banco
 * recebesse 408,2499, a conferência de aritmética reprovaria o cálculo certo.
 * Arredondar aqui faz o que aparece, o que é gravado e o que é conferido serem o
 * MESMO número.
 */
function centavos(v: number): number {
  return Math.round(v * 100) / 100;
}

function reais(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function calcular(p: Parametros, s: Simulacao): Resultado {
  const { classificacao, motivo } = classificar(s);

  const base =
    classificacao === "Complexa"
      ? p.base_complexa
      : classificacao === "Intermediária"
        ? p.base_intermediaria
        : p.base_simples;

  const adicionais: Item[] = [];

  if (s.dependentes > 0 && p.ad_dependente > 0) {
    adicionais.push({
      descricao: `${s.dependentes} ${s.dependentes === 1 ? "dependente" : "dependentes"} × ${reais(p.ad_dependente)}`,
      valor: centavos(s.dependentes * p.ad_dependente),
    });
  }
  if (s.fontes_renda > 1 && p.ad_fonte_renda > 0) {
    const extras = s.fontes_renda - 1;
    adicionais.push({
      descricao: `${extras} ${extras === 1 ? "fonte adicional" : "fontes adicionais"} × ${reais(p.ad_fonte_renda)}`,
      valor: centavos(extras * p.ad_fonte_renda),
    });
  }
  for (const x of SITUACOES) {
    // Situação marcada com adicional ZERO não entra na lista: linha "Carnê-leão
    // — R$ 0,00" faz o aluno procurar um erro que não existe. Ela continua
    // valendo para a CLASSIFICAÇÃO, que é o que a torna visível no resultado.
    if (s[x.campo] && p[x.parametro] > 0) {
      adicionais.push({ descricao: x.rotulo, valor: centavos(p[x.parametro]) });
    }
  }

  const adicionais_total = centavos(adicionais.reduce((soma, a) => soma + a.valor, 0));
  const antes = centavos(base + adicionais_total);

  const pos = POSICIONAMENTOS.find((x) => x.chave === s.posicionamento) ?? POSICIONAMENTOS[1];
  const ajuste = centavos(antes * pos.pct);
  const total = centavos(antes + ajuste);

  return {
    classificacao,
    motivo,
    base: centavos(base),
    adicionais,
    adicionais_total,
    posicionamento: pos.chave,
    posicionamento_rotulo: pos.rotulo,
    posicionamento_pct: pos.pct,
    ajuste,
    total,
    faixa_min: centavos(total * 0.95),
    faixa_max: centavos(total * 1.05),
    observacao: OBSERVACOES[classificacao],
  };
}

// ---------------------------------------------------------------------------
// A simulação nascendo do que o CLIENTE respondeu
// ---------------------------------------------------------------------------
/**
 * Uma linha de `irp_mapa_calculo()`.
 *
 * `calc_campo` é `string`, e não `CampoSimulacao`: o check do banco aceita os
 * nove nomes da simulação, e uma pergunta nova apontando para um campo que ESTA
 * versão do front ainda não tem é o caminho normal de evolução — o mapa é dado,
 * e dado muda sem deploy. Coagir o tipo aqui faria a tela confiar num nome que
 * ela não sabe usar; a guarda abaixo simplesmente ignora o que não reconhece.
 */
export type MapaCalculo = {
  codigo: string;
  calc_campo: string | null;
  calc_conta_fonte: boolean;
  calc_alerta: string | null;
  calc_alerta_quando: boolean | null;
};

export type PreEnchida = {
  simulacao: Simulacao;
  /** Campos que a resposta do cliente definiu — a tela marca estes. */
  doCliente: CampoSimulacao[];
  /** Campos que o formulário NÃO pergunta. A tela diz que o aluno decide. */
  semPergunta: CampoSimulacao[];
  /**
   * "Isto costuma dar mais trabalho". Vem de `irp_perguntas.calc_alerta` e não
   * mexe em preço nenhum: mudar valor por causa de uma resposta, sem o aluno
   * ver, é o que o princípio 2 do projeto chama de aplicar em silêncio.
   */
  alertas: string[];
  /**
   * Quantas fontes de renda as respostas SUGEREM. Nunca é fato: p4 é um pacote
   * ("salário, pró-labore, distribuição de lucros, aposentadoria ou pensão") e
   * pode valer uma fonte ou quatro. A tela mostra o número já no campo, editável,
   * e diz que é sugestão.
   */
  fontesSugeridas: number;
};

/** Todos os campos que a simulação tem, para dizer quais ficaram sem pergunta. */
const CAMPOS_DA_SIMULACAO: CampoSimulacao[] = [
  "dependentes",
  ...SITUACOES.map((x) => x.campo),
];

/** O nome que veio do banco é um campo que ESTA versão sabe preencher? */
function campoConhecido(nome: string | null): CampoSimulacao | null {
  return nome !== null && (CAMPOS_DA_SIMULACAO as string[]).includes(nome)
    ? (nome as CampoSimulacao)
    : null;
}

/**
 * A resposta vale? Só se a mãe dela foi "Sim".
 *
 * É a MESMA regra que o painel de respostas usa para desenhar
 * (`RespostasDoQuestionario` filtra por `respostas[q.pai]?.sim === true`), e a
 * igualdade é deliberada: calculadora que usa uma resposta que a tela esconde
 * cobraria por algo que o aluno não consegue ver na proposta.
 */
function respostaVale(
  codigo: string,
  perguntas: Pergunta[],
  respostas: Record<string, RespostaIrp>,
): boolean {
  let atual = perguntas.find((q) => q.codigo === codigo);
  // `irp_perguntas` é dado editável, e nada no banco impede um `pai` apontando
  // para si mesmo. Sem esta guarda a tela travaria num laço; COM ela, o ciclo faz
  // a resposta ser recusada — se a cadeia não pode ser verificada, o valor não
  // entra num preço. Recusar é o lado reversível: o campo fica para o aluno
  // marcar, em vez de cobrar por algo que ninguém confirmou.
  const visitados = new Set<string>([codigo]);
  while (atual?.pai) {
    if (visitados.has(atual.pai)) return false;
    visitados.add(atual.pai);
    if (respostas[atual.pai]?.sim !== true) return false;
    atual = perguntas.find((q) => q.codigo === atual!.pai);
  }
  return true;
}

export function daRespostas(
  mapa: MapaCalculo[],
  perguntas: Pergunta[],
  respostas: Record<string, RespostaIrp>,
): PreEnchida {
  const simulacao: Simulacao = { ...SIMULACAO_INICIAL };
  const doCliente: CampoSimulacao[] = [];
  const alertas: string[] = [];
  let fontes = 0;

  for (const linha of mapa) {
    const r = respostas[linha.codigo];
    if (!r || !respostaVale(linha.codigo, perguntas, respostas)) continue;

    if (linha.calc_conta_fonte && r.sim === true) fontes += 1;

    if (linha.calc_alerta !== null && r.sim === linha.calc_alerta_quando) {
      alertas.push(linha.calc_alerta);
    }

    // Campo que esta versão não conhece é IGNORADO, e não coagido: uma pergunta
    // nova apontando para um décimo campo entraria no banco sem deploy do front,
    // e escrever uma chave desconhecida na simulação a passaria adiante como
    // `undefined` — que atravessa a conta e reaparece como "R$ NaN".
    const campo = campoConhecido(linha.calc_campo);
    if (campo === null || r.sim === undefined) continue;

    if (campo === "dependentes") {
      // "Sim" sem número é "tem dependentes e não disse quantos". Zero seria uma
      // resposta que o cliente não deu; 1 é o mínimo que "Sim" garante.
      simulacao.dependentes = r.sim ? Math.max(1, Math.trunc(r.valor_numero ?? 1)) : 0;
    } else {
      simulacao[campo] = r.sim;
    }
    doCliente.push(campo);
  }

  const fontesSugeridas = Math.max(1, fontes);
  simulacao.fontes_renda = fontesSugeridas;

  return {
    simulacao,
    doCliente,
    semPergunta: CAMPOS_DA_SIMULACAO.filter(
      (c) => !mapa.some((linha) => linha.calc_campo === c),
    ),
    alertas,
    fontesSugeridas,
  };
}

// ---------------------------------------------------------------------------
// O que vai para o banco
// ---------------------------------------------------------------------------
/**
 * O detalhe que `irp_salvar_calculo` recebe e confere.
 *
 * As cinco chaves numéricas (`base`, `adicionais_total`, `ajuste`, `total` e o
 * array `adicionais`) são as que a RPC exige e cuja aritmética ela refaz. As
 * outras são registro: servem para o aluno explicar o preço meses depois, e para
 * a tela recarregar a simulação exatamente como estava.
 *
 * `calculado_em` NÃO vai daqui — quem carimba é o banco, com o relógio dele.
 */
export function paraOBanco(
  r: Resultado,
  s: Simulacao,
  doCliente: CampoSimulacao[],
): Record<string, unknown> {
  return {
    classificacao: r.classificacao,
    motivo: r.motivo,
    base: r.base,
    adicionais: r.adicionais,
    adicionais_total: r.adicionais_total,
    posicionamento: r.posicionamento,
    posicionamento_rotulo: r.posicionamento_rotulo,
    posicionamento_pct: r.posicionamento_pct,
    ajuste: r.ajuste,
    total: r.total,
    faixa_min: r.faixa_min,
    faixa_max: r.faixa_max,
    observacao: r.observacao,
    entradas: s,
    do_cliente: doCliente,
  };
}

/** O caminho de volta: o detalhe gravado virando simulação na tela. */
export function doBanco(detalhe: unknown): Simulacao | null {
  if (typeof detalhe !== "object" || detalhe === null) return null;
  const entradas = (detalhe as { entradas?: unknown }).entradas;
  if (typeof entradas !== "object" || entradas === null) return null;

  const e = entradas as Record<string, unknown>;
  const pos = e.posicionamento;
  const simulacao: Simulacao = {
    ...SIMULACAO_INICIAL,
    dependentes: typeof e.dependentes === "number" ? e.dependentes : 0,
    fontes_renda: typeof e.fontes_renda === "number" ? Math.max(1, e.fontes_renda) : 1,
    posicionamento:
      pos === "abaixo" || pos === "media" || pos === "acima" ? pos : "media",
  };
  for (const x of SITUACOES) {
    simulacao[x.campo] = e[x.campo] === true;
  }
  return simulacao;
}
